import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./ProductProfitReportPanel.css";

const SHOPS = [
  "ALL",
  "GMP",
  "OSHT",
  "OSPF",
  "MT",
  "JS",
  "JCHT",
  "JCPF",
  "MDYHT",
  "MDYPF",
];

function todayKey() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

function formatPercent(value) {
  const n = Number(value || 0);
  return `${n.toFixed(2)}%`;
}

export default function ProductProfitReportPanel({ onCreateCostFromMissing }) {
  const [date, setDate] = useState(todayKey());
  const [shop, setShop] = useState("ALL");
  const [view, setView] = useState("all");

  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [missingCostProducts, setMissingCostProducts] = useState([]);
  const [lowMarginProducts, setLowMarginProducts] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadReport() {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(
        `${API_BASE}/api/erp/product-profit-report?shop=${shop}&date=${date}`,
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to load product profit report");
      }

      setSummary(data.summary || {});
      setRows(data.rows || []);
      setMissingCostProducts(data.missingCostProducts || []);
      setLowMarginProducts(data.lowMarginProducts || []);
    } catch (err) {
      setError(err.message || "Product profit report loading failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, shop]);

  const displayRows = useMemo(() => {
    if (view === "missing") return missingCostProducts;
    if (view === "low") return lowMarginProducts;
    return rows;
  }, [view, rows, missingCostProducts, lowMarginProducts]);

  return (
    <div className="productProfit">
      <div className="productProfit__head">
        <div>
          <span>Product Profit Report</span>
          <h2>Sales vs Landed Cost</h2>
          <p>
            Product code, color and size အလိုက် sales amount, landed cost,
            gross profit and margin ကိုတွက်ပေးမယ်။
          </p>
        </div>

        <div className="productProfit__filters">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />

          <LocationSelect value={shop} onChange={setShop} mode="shop" includeAll />

          <button onClick={loadReport} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="productProfit__error">{error}</div>}

      <div className="productProfit__cards">
        <div className="productProfitCard">
          <span>Total Sales</span>
          <strong>{formatMoney(summary.totalSales)} Ks</strong>
        </div>

        <div className="productProfitCard">
          <span>Total Cost</span>
          <strong>{formatMoney(summary.totalCost)} Ks</strong>
        </div>

        <div className="productProfitCard productProfitCard--good">
          <span>Total Profit</span>
          <strong>{formatMoney(summary.totalProfit)} Ks</strong>
        </div>

        <div className="productProfitCard">
          <span>Margin</span>
          <strong>{formatPercent(summary.marginPercent)}</strong>
        </div>

        <div className="productProfitCard productProfitCard--danger">
          <span>Missing Cost</span>
          <strong>{formatMoney(summary.missingCostCount)}</strong>
        </div>

        <div className="productProfitCard productProfitCard--warn">
          <span>Low Margin</span>
          <strong>{formatMoney(summary.lowMarginCount)}</strong>
        </div>
      </div>

      <div className="productProfit__tabs">
        <button
          className={view === "all" ? "active" : ""}
          onClick={() => setView("all")}
        >
          All Products
        </button>

        <button
          className={view === "missing" ? "active" : ""}
          onClick={() => setView("missing")}
        >
          Missing Cost
        </button>

        <button
          className={view === "low" ? "active" : ""}
          onClick={() => setView("low")}
        >
          Low Margin
        </button>
      </div>

      {summary.missingCostCount > 0 && (
        <div className="productProfit__warning">
          ⚠️ Missing cost ရှိနေပါတယ်။ Product Profit အမှန်တွက်ချင်ရင်
          အောက်က Missing Cost item တွေအတွက် Import Cost Batch ထဲမှာ landed cost ထည့်ရပါမယ်။
        </div>
      )}

      <div className="productProfitTableWrap">
        <table className="productProfitTable">
          <thead>
            <tr>
              <th>Code</th>
              <th>Color</th>
              <th>Size</th>
              <th>Qty</th>
              <th>Sales</th>
              <th>Cost</th>
              <th>Profit</th>
              <th>Margin</th>
              <th>Unit Cost</th>
              <th>Batch</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {displayRows.length === 0 ? (
              <tr>
                <td colSpan="12" className="productProfitTable__empty">
                  No product profit data.
                </td>
              </tr>
            ) : (
              displayRows.slice(0, 80).map((row, index) => {
                const profit = Number(row.grossProfit || 0);
                const margin = Number(row.marginPercent || 0);
                const missing = row.costStatus === "MISSING_COST";

                return (
                  <tr key={`${row.productCode}-${row.color}-${row.size}-${index}`}>
                    <td>
                      <b>{row.productCode}</b>
                    </td>
                    <td>{row.color || "-"}</td>
                    <td>{row.size || "-"}</td>
                    <td>{formatMoney(row.qty)}</td>
                    <td>{formatMoney(row.salesAmount)} Ks</td>
                    <td>{formatMoney(row.costAmount)} Ks</td>
                    <td className={profit >= 0 ? "profitGood" : "profitBad"}>
                      {formatMoney(profit)} Ks
                    </td>
                    <td
                      className={
                        missing
                          ? "profitWarn"
                          : margin >= 20
                            ? "profitGood"
                            : margin >= 0
                              ? "profitWarn"
                              : "profitBad"
                      }
                    >
                      {formatPercent(row.marginPercent)}
                    </td>
                    <td>{formatMoney(row.unitLandedCost)} Ks</td>
                    <td>{row.batchNo || "-"}</td>
                    <td>
                      <span
                        className={
                          missing
                            ? "productProfitStatus productProfitStatus--missing"
                            : "productProfitStatus productProfitStatus--found"
                        }
                      >
                        {missing ? "NEED COST" : "COST OK"}
                      </span>
                    </td>

                    <td>
                      {missing ? (
                        <button
                          className="productProfitCreateCostBtn"
                          onClick={() => onCreateCostFromMissing?.(row)}
                        >
                          Create Cost
                        </button>
                      ) : (
                        <span className="productProfitNoAction">-</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


